import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProposalReceiptService } from 'app/services/http/proposal-receipt.service';
import { loadProposalBillingItems } from 'app/state/proposal-billing-item/proposal-billing-item.actions';
import { of } from 'rxjs';
import { catchError, map, switchMap, mergeMap } from 'rxjs/operators';
import {
    createProposalReceipt,
    createProposalReceiptFailure,
    createProposalReceiptSuccess,
    deleteProposalReceipt,
    deleteProposalReceiptFailure,
    deleteProposalReceiptSuccess,
    loadProposalReceipts,
    loadProposalReceiptsFailure,
    loadProposalReceiptsSuccess,
    updateProposalReceipt,
    updateProposalReceiptFailure,
    updateProposalReceiptSuccess,
} from './proposal-receipt.actions';

@Injectable()
export class ProposalReceiptEffects {
    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadProposalReceipts),
            switchMap(({ proposalId }) =>
                this.service.findAll(proposalId).pipe(
                    map((response) => loadProposalReceiptsSuccess({
                        proposalId,
                        receipts: response.data || [],
                        summary: response.summary
                    })),
                    catchError((error) => of(loadProposalReceiptsFailure({ error })))
                )
            )
        )
    );

    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createProposalReceipt),
            switchMap(({ proposalId, data }) =>
                this.service.create(proposalId, data).pipe(
                    map((receipt) => createProposalReceiptSuccess({ proposalId, receipt })),
                    catchError((error) => of(createProposalReceiptFailure({ error })))
                )
            )
        )
    );

    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateProposalReceipt),
            switchMap(({ proposalId, receiptId, data }) =>
                this.service.update(proposalId, receiptId, data).pipe(
                    map((receipt) => updateProposalReceiptSuccess({ proposalId, receipt })),
                    catchError((error) => of(updateProposalReceiptFailure({ error })))
                )
            )
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteProposalReceipt),
            switchMap(({ proposalId, receiptId }) =>
                this.service.delete(proposalId, receiptId).pipe(
                    map(() => deleteProposalReceiptSuccess({ proposalId, receiptId })),
                    catchError((error) => of(deleteProposalReceiptFailure({ error })))
                )
            )
        )
    );

    reloadRelated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createProposalReceiptSuccess, updateProposalReceiptSuccess, deleteProposalReceiptSuccess),
            mergeMap(({ proposalId }) => [
                loadProposalReceipts({ proposalId }),
                loadProposalBillingItems({ proposalId }),
            ])
        )
    );

    constructor(
        private actions$: Actions,
        private service: ProposalReceiptService
    ) { }
}
